/**
 * playSounds — synthesized short SFX for live play events.
 *
 * Designed to be:
 *   • LIGHTWEIGHT — zero asset downloads, pure Web Audio synth
 *   • SAFE — muted by default, requires explicit user opt-in (a click)
 *   • FORGIVING — every public method is a no-op when muted or when
 *     the AudioContext can't be created (e.g. private-mode quirks)
 *
 * Storage key `ds_play_sounds_enabled` (string "1" | absent). Default = muted.
 *
 * Public API:
 *   isEnabled()           → boolean
 *   setEnabled(true/false) → persists + resumes/suspends the audio context
 *   hit()                 → short bat-crack burst (~140ms)
 *   out()                 → descending thunk (~260ms)
 *
 * Browsers require a user gesture before an AudioContext can play sound.
 * Because the toggle button IS that gesture, we resume() inside setEnabled(true).
 */

const STORAGE_KEY = 'ds_play_sounds_enabled';

let _ctx = null;
const getCtx = () => {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    _ctx = new AC();
  } catch {
    return null;
  }
  return _ctx;
};

const isEnabled = () => {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; }
  catch { return false; }
};

const setEnabled = (val) => {
  try {
    if (val) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* private mode */ }
  const c = getCtx();
  if (c && val && c.state === 'suspended') {
    // resume() needs to be called inside the click handler, which it is.
    c.resume().catch(() => {});
  }
};

/**
 * Single short sine/square/triangle beep at the given frequency.
 * Internal helper — public methods compose these into "sounds".
 */
const beep = ({ freq = 440, duration = 0.1, type = 'sine', gain = 0.15, attack = 0.005 }) => {
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') {
    // Without a fresh user gesture we can't play — fail silent.
    return;
  }
  const t0 = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
};

/**
 * Hit / bat-crack: brief high-pitched square+triangle stack that decays fast.
 * Sounds like a crisp wooden contact, not a video-game ping.
 */
const hit = () => {
  if (!isEnabled()) return;
  beep({ freq: 880, duration: 0.08, type: 'square',   gain: 0.18 });
  setTimeout(() => beep({ freq: 440, duration: 0.10, type: 'triangle', gain: 0.12 }), 40);
};

/**
 * Out / thunk: a descending two-tone sine — feels like "shut it down".
 * Lower volume so back-to-back outs aren't grating.
 */
const out = () => {
  if (!isEnabled()) return;
  beep({ freq: 220, duration: 0.18, type: 'sine', gain: 0.13 });
  setTimeout(() => beep({ freq: 110, duration: 0.20, type: 'sine', gain: 0.10 }), 80);
};

const playSounds = { isEnabled, setEnabled, hit, out };
export default playSounds;
